/**
 * The A (start) and B (end) badges an embed can show when `ab=1`, on the map
 * and at the matching ends of the elevation profile, so a reader can tell
 * which end of the chart is which end of the route.
 */

export type EndpointKind = 'start' | 'end';

export interface EndpointBadge {
  label: string;
  color: string;
}

export const ENDPOINT_BADGES: Record<EndpointKind, EndpointBadge> = {
  start: { label: 'A', color: '#2E8B3E' },
  end: { label: 'B', color: '#E0702A' },
};

export const ENDPOINT_BADGE_CLASS = 'embed-endpoint-badge';

/** Radius of the badges drawn on the profile, in CSS pixels. */
export const PROFILE_BADGE_RADIUS = 8;

/**
 * The DOM element a map marker uses for one endpoint badge. The element itself
 * is just the circle, so the marker centres on it; a place name hangs off to
 * its right without moving the anchor.
 */
export function createEndpointBadgeElement(
  kind: EndpointKind,
  placeName: string | null = null,
  doc: Document = document,
): HTMLElement {
  const badge = ENDPOINT_BADGES[kind];
  const el = doc.createElement('div');
  el.className = `${ENDPOINT_BADGE_CLASS} ${ENDPOINT_BADGE_CLASS}--${kind}`;
  el.textContent = badge.label;
  el.setAttribute('aria-label', `${kind === 'start' ? 'Start' : 'End'}${placeName ? `: ${placeName}` : ''}`);
  Object.assign(el.style, {
    width: '22px',
    height: '22px',
    borderRadius: '50%',
    background: badge.color,
    border: '2px solid #fff',
    boxShadow: '0 1px 4px rgba(0, 0, 0, 0.4)',
    color: '#fff',
    font: '700 12px/18px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    textAlign: 'center',
    boxSizing: 'border-box',
    pointerEvents: 'none',
    // No `position` here: Mapbox positions markers absolutely via its own
    // class, which also anchors the name below. Setting it inline (relative)
    // drops the markers back into normal flow, where B pushes A down.
  });

  if (placeName) {
    const label = doc.createElement('span');
    label.className = `${ENDPOINT_BADGE_CLASS}__name`;
    label.textContent = placeName;
    Object.assign(label.style, {
      position: 'absolute',
      left: '24px',
      top: '50%',
      transform: 'translateY(-50%)',
      padding: '1px 6px',
      borderRadius: '4px',
      background: 'rgba(255, 255, 255, 0.92)',
      color: '#1a1b21',
      font: '600 11px/16px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      whiteSpace: 'nowrap',
      maxWidth: '240px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
    });
    el.appendChild(label);
  }
  return el;
}

/**
 * Centres for the profile's two badges: above the plot, at its left and right
 * edges — where the line starts and ends — nudged in so neither is clipped.
 */
export function profileBadgeCentres(
  chartArea: { top: number; left: number; right: number },
  canvasWidth: number,
  radius: number = PROFILE_BADGE_RADIUS,
): Record<EndpointKind, { x: number; y: number }> {
  const y = Math.max(radius, chartArea.top - radius - 2);
  return {
    start: { x: Math.max(radius, chartArea.left), y },
    end: { x: Math.min(canvasWidth - radius, chartArea.right), y },
  };
}

/** Extra top padding the chart needs so the badges sit above the plot. */
export const PROFILE_BADGE_PADDING = PROFILE_BADGE_RADIUS * 2 + 4;

interface MarkerLike {
  setLngLat(lngLat: [number, number]): MarkerLike;
  addTo(map: unknown): MarkerLike;
  remove(): unknown;
}

type MarkerFactory = (element: HTMLElement) => MarkerLike;

/**
 * Keeps at most one A and one B marker on the map. `set` replaces whatever was
 * there, so a view change (a single day or route of a hike) simply re-sets.
 */
export class EndpointBadgeMarkers {
  private markers: MarkerLike[] = [];

  constructor(private readonly map: unknown, private readonly createMarker: MarkerFactory) {}

  set(
    start?: [number, number] | null,
    end?: [number, number] | null,
    names: Partial<EndpointNames> = {},
  ): void {
    this.clear();
    // B first so A sits on top when a loop finishes where it started.
    if (end) this.add('end', end, names.end ?? null);
    if (start) this.add('start', start, names.start ?? null);
  }

  clear(): void {
    this.markers.forEach((marker) => marker.remove());
    this.markers = [];
  }

  get count(): number {
    return this.markers.length;
  }

  private add(kind: EndpointKind, lngLat: [number, number], placeName: string | null): void {
    const marker = this.createMarker(createEndpointBadgeElement(kind, placeName)).setLngLat(lngLat).addTo(this.map);
    this.markers.push(marker);
  }
}

/** The `[lon, lat]` of a GeoJSON point feature, or null if it isn't one. */
export function pointCoordinates(feature: any): [number, number] | null {
  const coords = feature?.geometry?.type === 'Point' ? feature.geometry.coordinates : null;
  if (!Array.isArray(coords) || typeof coords[0] !== 'number' || typeof coords[1] !== 'number') return null;
  return [coords[0], coords[1]];
}

/** The reverse-geocoded address fields the embeds fetch for a start or end. */
export interface EndpointAddress {
  name?: string | null;
  village?: string | null;
  town?: string | null;
  suburb?: string | null;
  city?: string | null;
  locality?: string | null;
  county?: string | null;
  country?: string | null;
}

export interface EndpointNameOptions {
  /** Append the country: "Clonegal, Ireland" (ab_country=1). */
  withCountry?: boolean;
}

// The geocoder's longest country names, shortened so a label stays a label.
const COUNTRY_SHORT_NAMES: Record<string, string> = {
  'United States of America': 'United States',
};

export interface EndpointNames {
  start: string | null;
  end: string | null;
}

// Ireland's geocoder fills `city` with the electoral division ("Lackan ED");
// the division is named after its townland, which is the useful part.
const ELECTORAL_DIVISION_SUFFIX = /\s+ED$/;

// `name` is sometimes a fine trailhead ("Sorrel Hill Car Park") and sometimes a
// full postal line, a street number or a grid code ("BV16"). Only take it when
// it reads as a place: no commas, no digits.
function placeLikeName(value: string): boolean {
  return !/[,\d]/.test(value);
}

/**
 * The most specific settlement in a start/end address, for labelling A and B.
 * Settlements beat the geocoder's `name`, which is too often a street address;
 * the county is the last resort. Null when there's nothing usable.
 */
export function endpointPlaceName(
  address: EndpointAddress | null | undefined,
  options: EndpointNameOptions = {},
): string | null {
  if (!address) return null;
  const clean = (value: string | null | undefined) => (value || '').trim();

  const city = clean(address.city).replace(ELECTORAL_DIVISION_SUFFIX, '');
  const name = clean(address.name);
  const candidates = [
    clean(address.village),
    clean(address.town),
    clean(address.suburb),
    city,
    clean(address.locality),
    placeLikeName(name) ? name : '',
    clean(address.county),
  ];
  const place = candidates.find((value) => value.length > 0) || null;
  if (!place || !options.withCountry) return place;

  const rawCountry = clean(address.country);
  const country = COUNTRY_SHORT_NAMES[rawCountry] || rawCountry;
  // Nothing to add when the country is unknown or the place already is it.
  if (!country || place.toLowerCase() === country.toLowerCase()) return place;
  return `${place}, ${country}`;
}

/**
 * Shortens `text` with an ellipsis until `measure` says it fits `maxWidth`.
 * Returns '' when not even the first character fits.
 */
export function truncateToWidth(text: string, maxWidth: number, measure: (value: string) => number): string {
  if (measure(text) <= maxWidth) return text;
  for (let length = text.length - 1; length > 0; length--) {
    const candidate = `${text.slice(0, length).trimEnd()}…`;
    if (measure(candidate) <= maxWidth) return candidate;
  }
  return '';
}

export interface EndpointAddressPair {
  start?: EndpointAddress | null;
  end?: EndpointAddress | null;
}

/**
 * Names for the A and B on screen. A is where the first shown route starts and
 * B where the last one ends, so each comes from its own route's address — a
 * single day or route of a hike gets that day's or route's ends. `fallback`
 * (the hike's own start/end) fills a gap, and is only passed for the whole
 * hike, whose ends it describes.
 */
export function endpointNamesForRoutes(
  startTrailId: string | number | null | undefined,
  endTrailId: string | number | null | undefined,
  routeAddresses: Record<string, EndpointAddressPair>,
  fallback: EndpointAddressPair | null = null,
  options: EndpointNameOptions = {},
): EndpointNames {
  const startRoute = startTrailId != null ? routeAddresses[String(startTrailId)] : undefined;
  const endRoute = endTrailId != null ? routeAddresses[String(endTrailId)] : undefined;
  return {
    start: endpointPlaceName(startRoute?.start, options) ?? endpointPlaceName(fallback?.start, options),
    end: endpointPlaceName(endRoute?.end, options) ?? endpointPlaceName(fallback?.end, options),
  };
}
